import type {
  NodeUnit,
  ResolvedValue,
  SlotInputNode,
  TearsheetDate,
} from 'types/packages-api/slot-valuation-inputs';
import { EMPTY, formatNodeDisplay } from './utils';

// ── "THIS VALUE WAS OVERRIDDEN", AND WHETHER THAT HAS REACHED THE NUMBERS ───────────────
//
// Two facts, and they are NOT the same fact:
//
//   overridden: true,  overrideApplied: true    the number beside this marker IS the override
//   overridden: true,  overrideApplied: false   the override is written down; the number is
//                                               still the old one, and stays so until the
//                                               evaluation is next calculated
//
// The second is the COMMON case, not an edge case. NINE of the eleven overridable fields are
// read out of a blob frozen at generation time, so an override on any of them reads `false`
// here until a re-run. (Measured: nine are `[:blob, …]`; the other two are
// `first_production_date` and `afe_source_id`, both parquet-bound, and they read the same way
// for the same reason.) A single "Overridden" badge on both would tell a user their edit had
// taken effect when the figure beside it disagrees — the precise misreading this marker
// exists to prevent.
//
// ABSENT `overridden` RENDERS NOTHING, and that covers two different absences: a node
// nothing can override, and a node whose row never arrived so we could not tell. Neither is
// a finding about this value, and inventing "not overridden" for the second would be a claim
// we cannot support. `overridden: false` — an explicit no — also renders nothing; the marker
// is there to flag a departure from the default, and every unedited row is one.
//
// How much of a pending value the badge shows before it truncates. Sized to the longest
// DATE the table renders (10 chars, `MM/DD/YYYY`) plus headroom, so no date is ever cut —
// only the free-text values (Volume Source names) that would otherwise push the row wide.
export const PENDING_VALUE_MAX = 22;

// NOTE ON COPY: "Overridden", "Override pending" and the two sentences below are NEW strings,
// not borrowed from the edit modals — the modals describe an action about to be taken and
// have nothing that describes a value's current state.
// ── A ROW THAT CANNOT CARRY AN OVERRIDE MUST NOT ADVERTISE ONE ──────────────────────────
//
// `scheduled_dates.json` is rewritten from the pace scenario on every run by
// DevelopmentPaceScenario::ScheduledDatesStep, which reads no override at all (measured
// 19 Sep 2026: zero references in that file), and the read path does not overlay it —
// SlotValuationInputs::BLOB_OVERLAID_NODES covers `[:blob, :slots]` nodes only. So
// permit_date, spud_date and completion_date can NEVER report applied, however many times
// the evaluation is re-run, and "Override pending" beside them is a promise the system
// cannot keep.
//
// The override is real; it is simply not this row's fact. The Milestones table renders the
// value the override does reach — the merger labels it `source: 'override'` — and that is
// the row the badge belongs on, which is why the milestone rendering passes `merged` and is
// never gated here.
//
// ⚠️ Making the scheduler honour overrides is a different change with a different blast
// radius. This one is only about not lying on screen.
//
// ── ⚠️ SERVED AS `overrideReachesRow`, NOT PATTERN-MATCHED HERE ─────────────────────────
//
// This used to test the node's `source` tuple for `['blob', 'scheduled_slots']`. That is a
// fact about the SERVER's read path — which blob the overlay covers and which step rewrites
// which artifact — and stating it on the frontend meant the two could drift with nothing to
// catch it. The server now answers the question itself, on every node.
//
// ABSENT READS AS TRUE, which is what the tuple test already did for a node whose definition
// had not arrived: `Array.isArray(undefined)` is false, so the old predicate returned true
// too. Behaviour is unchanged in that window; only the source of the answer moved.
export function rowCanCarryOverride(meta?: {
  overrideReachesRow?: boolean;
}): boolean {
  return meta?.overrideReachesRow ?? true;
}

/**
 * The override marker's sentence: what this value is, and what it was.
 *
 * ── PURE, AND EXTRACTED FOR THAT REASON ──────────────────────────────────────────────────
 *
 * It was four template literals inside OverrideMarker, which meant the only way to see what
 * the page says was to render it. Four sentences over two booleans is a decision table, and a
 * decision table wants reading without a DOM.
 *
 * ⚠️ THE COPY IS APPROVED. The strings are asserted byte for byte in tearsheet-cells.test.tsx;
 * change them only with sign-off.
 *
 * ── THE SECOND ARGUMENT IS THE PREVIOUS VALUE, NOT A FLAG ────────────────────────────────
 *
 * `null` means no previous value was RECORDED, which is a different fact from the previous
 * value having been blank — the caller distinguishes them with `'overriddenDefault' in entry`
 * before ever calling this, because a truthiness test collapses the two and the page then
 * prints "was —" over a value nobody captured. Passing the formatted string or null keeps
 * that decision with the caller, which is where the payload is.
 *
 * @param {boolean} applied - Whether the run computed with this override.
 * @param {string | null} previous - The formatted prior value, or null when none was recorded.
 * @return {string} The sentence to show.
 */
export function overrideMarkerCopy(
  applied: boolean,
  previous: string | null
): string {
  if (applied) {
    return previous === null
      ? 'This value is overridden. The value it had beforehand was not recorded, so it cannot be shown.'
      : `This value is overridden. It was ${previous} before the override.`;
  }
  return previous === null
    ? 'An override is recorded for this value but is not in these numbers yet. It applies when this evaluation is next calculated.'
    : `An override is recorded for this value but is not in these numbers yet. It applies when this evaluation is next calculated. Until then this reads ${previous}.`;
}

/** What an overridden row's badge says, before any page-only truncation or actions. */
export interface OverrideBadge {
  /** The overridden value entry the badge describes. */
  entry: ResolvedValue;
  /** Whether the run computed with this override. */
  applied: boolean;
  /** Whether the payload recorded the value from before the override. */
  hasDefault: boolean;
  /** That previous value, formatted, or null when none was recorded. */
  previous: string | null;
  /** The toggletip's sentence — {@link overrideMarkerCopy}. */
  explanation: string;
  /** The incoming value on a pending override, untruncated; null when there is none to name. */
  pendingValue: string | null;
}

/**
 * The override badge for one row, or null when the row shows none.
 *
 * ONE DERIVATION FOR THE PAGE AND THE WORKBOOK. OverrideMarker renders it, and the export
 * folds the same text into the Source cell, so a pending override cannot be marked on screen
 * and silent in the file.
 *
 * @param {SlotInputNode} node - The row's node.
 * @param {NodeUnit} [unit] - Its served unit.
 * @param {object} [options] - `merged`: the Milestones row's merged date; `meta`: the
 *   node's served definition (only `overrideReachesRow` is read).
 * @return {(OverrideBadge|null)} The badge, or null.
 */
export function overrideBadge(
  node: SlotInputNode,
  unit: NodeUnit | undefined,
  {
    merged,
    meta,
  }: {
    merged?: TearsheetDate;
    meta?: { overrideReachesRow?: boolean };
  } = {}
): OverrideBadge | null {
  const entry = node.values.find((value) => value.overridden === true);
  if (!entry) {
    return null;
  }
  // NODE-BOUND ROWS ONLY. `merged` marks the milestone rendering, whose row does carry the
  // override, so it is never suppressed here.
  if (!merged && !rowCanCarryOverride(meta)) {
    return null;
  }
  // ⚠️ `overrideApplied`, NEVER `merged.source`. The merger's source is a READ-TIME fact —
  // it says the overlay used the override to choose this value on this read, and it is true
  // the moment an override exists. Binding the badge to it claimed "Overridden" on a value no
  // run had seen (measured: saved, nothing run, both date rows read Overridden).
  //
  // The server now answers the run-time question on the merged date itself, from what the
  // calculation recorded being sent — see SlotValuationInputs#attach_override_applied. Absent
  // reads as false, which is the safe direction: a payload from before that key existed says
  // "pending" rather than inventing an application.
  const applied = merged
    ? merged.overrideApplied === true
    : entry.overrideApplied === true;
  // `'overriddenDefault' in entry` rather than a truthiness or null test, for the same reason
  // the zone-domain gate uses it: the key is ABSENT when no previous value was recorded, and
  // present-with-null when the previous value genuinely was blank. Read as truthiness those
  // become one state, and the page would print "was —" over a value nobody captured.
  const hasDefault = 'overriddenDefault' in entry;
  const previous = hasDefault
    ? formatNodeDisplay(node.key, entry.overriddenDefault ?? null, unit)
    : null;
  const explanation = overrideMarkerCopy(applied, previous);
  // ── WHAT THE OVERRIDE WILL APPLY ──────────────────────────────────────────────────────
  //
  // A DIFFERENT FACT FROM THE TOGGLETIP'S. The toggletip says what a revert would restore
  // ("this reads <previous>"); the badge says what the next calculation will write. Nine of
  // the eleven overridable fields are blob-sourced, so the row goes on showing the OLD value
  // until that run — which has already been read as a bug once. Naming the incoming value on
  // the badge answers it without opening anything.
  //
  // PENDING ONLY. An applied override's value is already the one in the row beside it, so
  // repeating it there would say the same thing twice.
  //
  // ⚠️ READ `overrideValue`, NEVER `entry.value`. While an override is unapplied `entry.value`
  // is still the PRE-override number — that is the shipped contract — so naming it here
  // printed the old value as though it were the incoming one (measured: the log
  // said 2028-06-01, the badge said 04/01/2028). ABSENT is not nil: a payload that predates
  // the key says nothing, and the badge falls back to its bare form rather than guessing.
  const pendingCandidate =
    applied || !('overrideValue' in entry)
      ? null
      : formatNodeDisplay(node.key, entry.overrideValue ?? null, unit);
  // ── NOT WHEN THE CELL BESIDE IT ALREADY SHOWS IT ──────────────────────────────────────
  //
  // The arrow exists because the row usually shows the OLD value while an override waits for
  // a run. For eight of the eleven fields it does not: the read path overlays the slot row
  // and the slots blob, so the cell already shows the override and the badge would point at
  // the number it is printed next to — which is what a reader reported as a bug, and is the
  // same "says it twice" the applied state avoids by not naming a value at all.
  //
  // Compared as DISPLAY TEXT, not as values: `value` and `overrideValue` reach this component
  // from different stores and differ in type (a date arrives as a string from the log and can
  // arrive as a Date from a blob), and it is the rendered string the reader is comparing.
  // ⚠️ THE CELL THIS ROW PRINTS, not the node's value. On a milestone row those differ —
  // the cell is the merger's answer — and comparing against the node left the arrow pointing
  // at the number printed beside it.
  const renderedValue = merged
    ? formatNodeDisplay(node.key, merged.value ?? null, unit)
    : formatNodeDisplay(node.key, entry.value, unit);
  const pendingValue =
    pendingCandidate !== null && pendingCandidate === renderedValue
      ? null
      : pendingCandidate;
  return { entry, applied, hasDefault, previous, explanation, pendingValue };
}

/**
 * The badge's visible text: "Overridden", "Override pending → <value>" or "Override pending".
 *
 * @param {boolean} applied - Whether the run computed with the override.
 * @param {(string|null)} pendingValue - The incoming value to name, or null.
 * @return {string} The text.
 */
export function overrideBadgeText(
  applied: boolean,
  pendingValue: string | null
): string {
  if (applied) {
    return 'Overridden';
  }
  return pendingValue && pendingValue !== EMPTY
    ? `Override pending → ${pendingValue}`
    : 'Override pending';
}
